/**
 * Description: /internal/subscribe — SSR-embeds the Berita Harian eShop
 * precheckout partial via @sphtech/eshop-partials-embed. The loader pre-fetches
 * the fragment + embed.json manifest; `meta` renders the SEO tags plus the
 * manifest's preload <link> tags into <head> (so WebCore hoists the partial's
 * render-blocking Panda stylesheet ahead of the shadow content); PrecheckoutMount
 * renders the fragment inside the standard BH page chrome, falling back to the
 * CSR contract when the fragment is unavailable.
 *
 * No Figma frame: this route is a server-side embed of the eShop precheckout
 * partial, whose UI is owned by the eShop team — there is no BH design to link.
 */
import useInlineLoginUserInfo from "@hooks/useInlineLoginUserInfo";
import { type Route } from "@route-types/Subscribe/+types/SubscribePage";
import BHSubscriptionPage from "@routes/BHSubscriptionPage";
import NotFound from "@routes/NotFound";
import { PrecheckoutMount } from "@sphtech/eshop-partials-embed/precheckout/mount";
import { DEFAULT_PARTIAL_VERSION } from "@sphtech/eshop-partials-embed/precheckout/subscribe-constants";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { ReactElement } from "react";
import { type ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import { loadSubscribe } from "./SubscribePage.server";
import { subscribeMeta } from "./SubscribePageMetaData";
import type { SubscribeContext } from "./utils/types";

const SubscribePage = (): ReactElement => {
  const context: SubscribeContext = useLoaderData();
  useInlineLoginUserInfo();

  if (context.type !== ResponseType.SUCCESS) {
    return <NotFound />;
  }

  const { html, assetsBase, locale, publication } = context.payload;

  return (
    <BHSubscriptionPage>
      <PrecheckoutMount
        html={html}
        assetsBase={assetsBase}
        locale={locale}
        publication={publication}
        className="subscribe-container"
      />
    </BHSubscriptionPage>
  );
};

export default SubscribePage;

export const loader = async (): Promise<SubscribeContext> => {
  return loadSubscribe(DEFAULT_PARTIAL_VERSION);
};

export const meta = ({ loaderData }: Route.MetaArgs) =>
  subscribeMeta(loaderData);

// Without a clientLoader, SPA navigation to this route 404s. Any fetch failure
// falls back to the server loader so navigation still resolves.
export const clientLoader = async (
  args: ClientLoaderFunctionArgs,
): Promise<SubscribeContext> => {
  try {
    const result = (await fetchContext(args)) as { context?: SubscribeContext };
    return result.context ?? (await args.serverLoader());
  } catch {
    return await args.serverLoader();
  }
};
